import { useState, useMemo, useCallback } from 'react';
import { User } from '../services/AdminDataService';

export interface FilterState {
  username: string;
  fullName: string;
  email: string;
  role: string;
  supervisor: string;
  status: 'all' | 'active' | 'inactive';
  lastLoginFrom: string;
  lastLoginTo: string;
  globalSearch: string;
}

export interface FilterOptions {
  availableRoles: string[];
  availableSupervisors: Array<{ id: string; name: string; role: string }>;
}

const initialFilterState: FilterState = {
  username: '',
  fullName: '',
  email: '',
  role: '',
  supervisor: '',
  status: 'all',
  lastLoginFrom: '',
  lastLoginTo: '',
  globalSearch: ''
};

export const useUserFilters = (users: User[], options: FilterOptions) => {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);

  // Update individual filter
  const updateFilter = useCallback((field: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'status') return value !== 'all';
      return value !== '';
    });
  }, [filters]);

  // Optimized filtering logic with memoization
  const filteredUsers = useMemo(() => {
    if (!hasActiveFilters) return users;

    return users.filter(user => {
      // Global search - searches across all text fields
      if (filters.globalSearch) {
        const searchTerm = filters.globalSearch.toLowerCase().trim();
        if (searchTerm) {
          const searchableText = [
            user.username,
            user.full_name || '',
            user.email,
            user.role,
            user.supervisor_name || '',
            user.is_active ? 'active' : 'inactive'
          ].join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerm)) {
            return false;
          }
        }
      }

      // Username filter
      if (filters.username) {
        const username = user.username.toLowerCase();
        const searchTerm = filters.username.toLowerCase().trim();
        if (!username.includes(searchTerm)) {
          return false;
        }
      }

      // Full name filter
      if (filters.fullName) {
        const fullName = (user.full_name || '').toLowerCase();
        const searchTerm = filters.fullName.toLowerCase().trim();
        if (!fullName.includes(searchTerm)) {
          return false;
        }
      }

      // Email filter
      if (filters.email) {
        const email = user.email.toLowerCase();
        const searchTerm = filters.email.toLowerCase().trim();
        if (!email.includes(searchTerm)) {
          return false;
        }
      }

      // Role filter
      if (filters.role) {
        if (user.role !== filters.role) {
          return false;
        }
      }

      // Supervisor filter
      if (filters.supervisor) {
        if (filters.supervisor === 'no_supervisor') {
          // Filter for users with no supervisor
          if (user.supervisor_id) {
            return false;
          }
        } else {
          // Filter for users with specific supervisor
          if (user.supervisor_id !== filters.supervisor) {
            return false;
          }
        }
      }

      // Status filter
      if (filters.status !== 'all') {
        const userStatus = user.is_active ? 'active' : 'inactive';
        if (userStatus !== filters.status) {
          return false;
        }
      }

      // Last login date range filter
      if (filters.lastLoginFrom || filters.lastLoginTo) {
        if (!user.last_login) {
          // If user has never logged in, only show if no date filters are set
          return false;
        }

        const lastLoginDate = new Date(user.last_login);
        
        if (filters.lastLoginFrom) {
          const fromDate = new Date(filters.lastLoginFrom);
          if (lastLoginDate < fromDate) {
            return false;
          }
        }

        if (filters.lastLoginTo) {
          const toDate = new Date(filters.lastLoginTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (lastLoginDate > toDate) {
            return false;
          }
        }
      }

      return true;
    });
  }, [users, filters, hasActiveFilters]);

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    return options.availableRoles.length > 0 
      ? options.availableRoles
      : Array.from(new Set(users.map(user => user.role))).sort();
  }, [users, options.availableRoles]);

  // Filter statistics
  const filterStats = useMemo(() => {
    return {
      total: users.length,
      filtered: filteredUsers.length,
      hidden: users.length - filteredUsers.length
    };
  }, [users.length, filteredUsers.length]);

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredUsers,
    uniqueRoles,
    filterStats
  };
};